import React from "react";
import { useTranslation } from "react-i18next";

import { Stepper, Step, StepLabel, StepContent } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleXmark, faCircleInfo } from "@fortawesome/pro-solid-svg-icons";
import styled, { useTheme } from "styled-components";

import moment from "moment";
import {
  UpfrontPaymentStepperWrapper,
  UpfrontPaymentStepperRow,
  UpfrontPaymentStepperRowDate,
  UpfrontPaymentStepperRowCost,
  UpfrontPaymentLink,
} from "./UpfrontPayment.styles";
import { BillingInfoTooltip } from "./BillingInfoTooltip";

import {
  UpfrontPaymentPlan,
  getCentsToDollars,
} from "./Services/UpfrontPaymentService";
import { PaymentPendingCircle } from "../../assets/iconComponents/PaymentPendingCircle";
import { PaymentCompleteCircle } from "../../assets/iconComponents/PaymentCompleteCircle";

export const UpfrontPaymentPlanSteps: React.FC<{
  steps: UpfrontPaymentPlan[];
  onPayNowClick?: (installment: UpfrontPaymentPlan) => void;
}> = ({ steps, onPayNowClick }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const getPaymentStatusIcon = (payment: UpfrontPaymentPlan) => {
    const isFailedOrRetryable =
      payment.stripe_sync_status === "failed" ||
      payment.stripe_sync_status === "retryable";

    if (
      payment.status === "paid" ||
      payment.stripe_sync_status === "succeeded"
    ) {
      return PaymentCompleteCircle;
    }

    if (isFailedOrRetryable) {
      return () => (
        <FontAwesomeIcon icon={faCircleXmark} color={theme.redDark} />
      );
    }

    return PaymentPendingCircle;
  };

  const stepComponents = steps?.map((payment) => {
    const isFailedOrRetryable =
      payment.stripe_sync_status === "failed" ||
      payment.stripe_sync_status === "retryable";

    return {
      component: (
        <UpfrontPaymentStepperRow>
          <UpfrontPaymentStepperRowDate $isFailed={isFailedOrRetryable}>
            {moment(new Date(payment.epoch * 1000)).format(
              "MMMM Do, YYYY",
            )}{" "}
          </UpfrontPaymentStepperRowDate>
          {isFailedOrRetryable && (
            <BillingInfoTooltip
              compact
              label={t("upfrontPayment.paymentPlan.paymentMethodFailed")}
              content={
                <>
                  {t("upfrontPayment.paymentPlan.paymentMethodFailed")}
                  {payment.stripe_sync_status === "retryable" &&
                    t("upfrontPayment.paymentPlan.retryNotice")}
                </>
              }
              icon={
                <FontAwesomeIcon
                  icon={faCircleInfo}
                  color={theme.greyMedium}
                  style={{ width: "14px", height: "14px" }}
                />
              }
              placement="top-start"
            />
          )}
          {/* Spacer to push amount to the right */}
          <div style={{ marginLeft: "auto" }} />
          {isFailedOrRetryable && onPayNowClick && (
            <UpfrontPaymentLink
              onClick={() => onPayNowClick(payment)}
              style={{ marginRight: "12px" }}
            >
              {t("upfrontPayment.paymentPlan.payNow")}
            </UpfrontPaymentLink>
          )}
          <UpfrontPaymentStepperRowCost>{`${getCentsToDollars(
            payment.amount,
          )}`}</UpfrontPaymentStepperRowCost>
        </UpfrontPaymentStepperRow>
      ),
      payment,
    };
  });

  return (
    <UpfrontPaymentStepperWrapper>
      <Stepper activeStep={0} orientation="vertical">
        {stepComponents?.map((step) => (
          <Step key={step.payment.epoch}>
            <StepLabel StepIconComponent={getPaymentStatusIcon(step.payment)}>
              {step.component}
            </StepLabel>
            <StepContent />
          </Step>
        ))}
      </Stepper>
    </UpfrontPaymentStepperWrapper>
  );
};
